import { Wallet, Percent, HandCoins } from "lucide-react";
import useTranslations from "@/shared/lang/useTranslations";
import type { QuoterQuoteData } from "../types/quoter.types";

type QuoterSummaryProps = {
  quote: QuoterQuoteData | null;
  quoteError: string | null;
  hideCommissionLine?: boolean;
  hideConvertedRow?: boolean;
};

function formatAmount(value: number): string {
  return value.toLocaleString("es-CO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export const QuoterSummary = ({ quote, quoteError, hideCommissionLine = false, hideConvertedRow = false }: QuoterSummaryProps) => {
  const { t } = useTranslations("quoter");
  const fromCurrency = quote?.currency.from ?? "—";
  const toCurrency = quote?.currency.to ?? "—";
  const totalToPay = quote
    ? formatAmount(quote.breakdown.amount_sent)
    : "—";
  const commissionAmount = quote
    ? formatAmount(quote.fees.total_fee)
    : "—";
  const amountConverted = quote
    ? formatAmount(quote.breakdown.amount_received + quote.fees.total_fee)
    : "—";
  const beneficiaryReceives = quote
    ? formatAmount(quote.breakdown.amount_received)
    : "—";

  return (
    <div className="bg-green-50/80 dark:bg-green-500/10 rounded-2xl p-6 border border-green-500/20 mt-2">
      {quoteError && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <p className="text-sm text-red-600 dark:text-red-400">{quoteError}</p>
        </div>
      )}
      <div className="flex flex-col">
        <div className="flex justify-between items-start pb-4 border-b border-dashed border-gray-300 dark:border-zinc-600">
          <span className="flex items-center gap-2 text-gray-700 dark:text-zinc-300 font-medium">
            <Wallet className="size-5 text-green-600 dark:text-green-400 shrink-0" aria-hidden />
            {t("summary_total_pay")}
          </span>
          <div className="text-right">
            <span className="block text-xl font-bold text-emerald-700 dark:text-green-400">
              {totalToPay} {fromCurrency}
            </span>
            {!hideCommissionLine && (
              <span className="flex items-center gap-1 justify-end mt-0.5 text-sm text-gray-500 dark:text-zinc-400">
                <Percent className="size-3.5 shrink-0" aria-hidden />
                -{commissionAmount} {toCurrency} {t("summary_commission_suffix", { percent: quote?.fees.percentage_fee ?? "—" })}
              </span>
            )}
          </div>
        </div>
        {!hideConvertedRow && (
          <div className="flex justify-between items-center py-3 border-b border-dashed border-gray-300 dark:border-zinc-600">
            <span className="text-sm text-gray-600 dark:text-zinc-400">
              {t("summary_amount_converted")}
            </span>
            <span className="text-sm font-medium text-gray-800 dark:text-zinc-200">
              {amountConverted} {toCurrency}
            </span>
          </div>
        )}
        <div className="flex justify-between items-center pt-4">
          <span className="flex items-center gap-2 text-gray-700 dark:text-zinc-300 font-semibold">
            <HandCoins className="size-5 text-green-600 dark:text-green-400 shrink-0" aria-hidden />
            {t("summary_beneficiary_receives")}
          </span>
          <span className="text-xl font-bold text-emerald-700 dark:text-green-400">
            {beneficiaryReceives} {toCurrency}
          </span>
        </div>
      </div>
    </div>
  );
};
